import { mount, unmount } from "svelte";
import { ISLAND_REGISTRY } from "./registry";
import { sharedQueryClient } from "~/lib/query-client";
import { parseIslandProps } from "./parse-island-props";
import type { IslandModule } from "~/types/islands";

export function setupThemeEditorEvents(handlers: {
  onLoad: (section: HTMLElement) => void;
  onUnload: (section: HTMLElement) => void;
  onReorder: () => void;
}): void {
  if (!window.Shopify?.designMode) return;
  document.addEventListener("shopify:section:load", (event) => {
    handlers.onLoad((event as CustomEvent).target as HTMLElement);
  });
  document.addEventListener("shopify:section:unload", (event) => {
    handlers.onUnload((event as CustomEvent).target as HTMLElement);
  });
  document.addEventListener("shopify:section:reorder", () => {
    handlers.onReorder();
  });
}

/**
 * WeakMap to track mounted island instances
 * Automatically garbage collected when DOM elements are removed
 */
const islandInstances = new WeakMap<Element, ReturnType<typeof mount>>();

/**
 * Build lookup map from registry for fast access
 */
const islandLookup = ISLAND_REGISTRY.reduce((map, registration) => {
  if (map.has(registration.name)) {
    console.error(`Duplicate island name: "${registration.name}"`);
  }
  map.set(registration.name, registration.importer);
  return map;
}, new Map<string, () => Promise<IslandModule>>());

if (import.meta.env.DEV) {
  // Log registered islands
  console.log("Registered islands:", Array.from(islandLookup.keys()).sort().join(", ") || "none");
}

/**
 * Mount islands found within a container element
 *
 * Scans for elements with data-island="[island-name]" attribute
 * and dynamically imports + mounts the corresponding island component
 *
 * @param container - Container element to scan for islands
 * @param options - Mount options
 */
export async function mountIsland(
  container: HTMLElement | Document,
  options: { lazy?: boolean } = {},
): Promise<void> {
  const elements = container.querySelectorAll("[data-island]");

  if (import.meta.env.DEV) {
    console.log(`Found ${elements.length} islands to mount`);
  }

  for (const element of Array.from(elements)) {
    // Skip if already mounted
    if (islandInstances.has(element)) {
      continue;
    }

    const islandName = element.getAttribute("data-island");

    if (!islandName) {
      console.warn("[Aurora] Island element missing data-island attribute:", element);
      continue;
    }

    // Skip islands that have dedicated entrypoints — they mount themselves.
    if (!islandLookup.has(islandName)) continue;

    // Determine if island should be lazy-loaded
    const shouldLazyMount = options.lazy ?? isElementBelowFold(element as HTMLElement);

    if (shouldLazyMount) {
      lazyMountIsland(element as HTMLElement, islandName);
    } else {
      await mountIslandElement(element as HTMLElement, islandName);
    }
  }
}

/**
 * Unmount islands within a container element
 *
 * Used primarily for theme editor section unload events
 *
 * @param container - Container element to unmount islands from
 */
export function unmountIsland(container: HTMLElement | Document): void {
  const elements = container.querySelectorAll("[data-island]");

  if (import.meta.env.DEV) {
    console.debug(`Unmounting ${elements.length} islands`);
  }

  for (const element of Array.from(elements)) {
    const instance = islandInstances.get(element);

    if (instance) {
      try {
        unmount(instance);
        islandInstances.delete(element);

        const islandName = element.getAttribute("data-island");
        if (import.meta.env.DEV) {
          console.debug(`Unmounted island: ${islandName}`);
        }
      } catch (error) {
        console.error("Error unmounting island:", error);
      }
    }
  }
}

/**
 * Mount a single island element
 *
 * @param element - DOM element to mount island on
 * @param islandName - Name of the island to mount
 */
async function mountIslandElement(element: HTMLElement, islandName: string): Promise<void> {
  try {
    if (import.meta.env.DEV) {
      console.debug(`Mounting island: ${islandName}`);
    }

    // Look up island importer — presence is guaranteed by the outer mountIsland guard
    const islandImporter = islandLookup.get(islandName)!;

    // Parse props from data-props-* attributes and/or data-props JSON,
    // then inject the island name and shared QueryClient so all islands
    // share the same query cache.
    const props = {
      ...parseIslandProps(element),
      name: islandName,
      queryClient: sharedQueryClient,
    };

    // Dynamic import island component
    const { default: Island } = await islandImporter();

    // Empty island slot (in case placeholder content is present)
    element.innerHTML = "";

    // Mount island
    const instance = mount(Island, {
      target: element,
      props,
    });

    // Track instance for cleanup
    islandInstances.set(element, instance);

    if (import.meta.env.DEV) {
      console.log(`Mounted island ${islandName} with props: `, props);
    }
  } catch (error) {
    console.error(`Error mounting island "${islandName}":`, error);
  }
}

/**
 * Lazy mount island using IntersectionObserver
 *
 * @param element - DOM element to mount island on
 * @param islandName - Name of the island to mount
 */
function lazyMountIsland(element: HTMLElement, islandName: string): void {
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          mountIslandElement(element, islandName);
          observer.unobserve(element);
        }
      });
    },
    { threshold: 0.1 }, // Mount when 10% visible
  );

  observer.observe(element);

  if (import.meta.env.DEV) {
    console.debug(`Lazy mounting island: ${islandName}`);
  }
}

/**
 * Check if element is below the fold (not in initial viewport)
 *
 * @param element - Element to check
 * @returns True if element is below the fold
 */
function isElementBelowFold(element: HTMLElement): boolean {
  const rect = element.getBoundingClientRect();
  const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
  return rect.top > viewportHeight;
}
